import { Box, Card, Stack, Typography } from '@mui/material';
import { useGetAnnouncementLog } from '~/services/api/announcement';
import { useMemo, useState } from 'react';
import moment from 'moment-timezone';
import { theme, Icon } from 'bs-unified-ui';
import { Icon as CfIcon } from '~/components/common/Icon';
import {
  TIMEZONE,
  POS_TYPE_OPTIONS,
  PROVINCE_TYPE_OPTIONS,
  DISMISS_BEHAVIOR_POPUP_OPTIONS,
  DISMISS_BEHAVIOR_BANNER_OPTIONS,
  ANNOUNCEMENT_TYPE_OPTIONS,
} from '~/constants';

type RenderValue = string | React.ReactNode;

type LogItem = {
  _id: string;
  title: string;
  created_by_name?: string;
  created_at: string;
  old_data?: any;
  new_data?: any;
};

type GroupedTimeline = {
  date: string;
  items: LogItem[];
};

const TimelineSection = ({ announcementId }: { announcementId?: string }) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const { data: logData } = useGetAnnouncementLog({
    enabled: !!announcementId,
    id: announcementId,
  });

  const groupedTimeline = useMemo(() => {
    if (!logData || !Array.isArray(logData)) {
      return [];
    }

    const groups: { [key: string]: LogItem[] } = {};

    logData.forEach((item: LogItem) => {
      const dateKey = moment
        .utc(item.created_at)
        .tz(TIMEZONE)
        .format('MMM D, YYYY');
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(item);
    });

    // Convert to array and sort by date descending
    return Object.entries(groups)
      .map(([date, items]) => ({
        date,
        items: items.sort(
          (a, b) =>
            moment.utc(b.created_at).valueOf() -
            moment.utc(a.created_at).valueOf(),
        ),
      }))
      .sort((a, b) => {
        const dateA = moment.utc(a.date, 'MMM D, YYYY').valueOf();
        const dateB = moment.utc(b.date, 'MMM D, YYYY').valueOf();
        return dateB - dateA;
      });
  }, [logData]);

  const formatDate = (timestamp: string) => {
    try {
      return moment.utc(timestamp).tz(TIMEZONE).format('MMM DD, YYYY');
    } catch {
      return timestamp;
    }
  };

  const formatTime = (timestamp: string) => {
    try {
      const zonedTime = moment.utc(timestamp).tz(TIMEZONE);
      const time = zonedTime.format('h:mm');
      const suffix = zonedTime.format('A'); // 'A' returns 'AM' or 'PM'
      const formattedSuffix = `${suffix[0].toLowerCase()}.${suffix[1].toLowerCase()}.`; // 'AM' -> 'a.m.', 'PM' -> 'p.m.'
      return `${time} ${formattedSuffix}`;
    } catch {
      return timestamp;
    }
  };

  const formatStartEndTime = (timestamp: string) => {
    try {
      // Parse timestamp without timezone conversion (it's already in the correct format)
      const date = moment(timestamp).format('MMM D, YYYY');
      const time = moment(timestamp).format('h:mm');
      const suffix = moment(timestamp).format('A');
      const formattedSuffix = `${suffix[0].toLowerCase()}.${suffix[1].toLowerCase()}.`;
      return `${date} ${time} ${formattedSuffix}`;
    } catch {
      return timestamp;
    }
  };

  const getChangedFields = (oldData: any, newData: any) => {
    const changes: { [key: string]: { old: any; new: any } } = {};

    // Only check fields that exist in newData
    Object.keys(newData || {}).forEach((key) => {
      const oldVal = oldData?.[key];
      const newVal = newData?.[key];

      // Skip internal fields and null comparisons
      if (
        ![
          '_id',
          'created_by',
          'created_by_name',
          'created_at',
          'updated_at',
        ].includes(key) &&
        JSON.stringify(oldVal) !== JSON.stringify(newVal)
      ) {
        changes[key] = { old: oldVal, new: newVal };
      }
    });

    return changes;
  };

  const toggleExpand = (id: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const getFieldLabel = (fieldName: string) => {
    const fieldLabelMap: { [key: string]: string } = {
      message_description: 'Description',
      message_title: 'Title',
      apply_to_pos: 'POS type',
      apply_to_provinces_type: 'Provinces',
      image: 'Image',
      type: 'Type',
      banner_color: 'Banner color',
      icon: 'Banner icon',
      dismiss_behavior: 'Dismiss behavior',
      dismiss_behaviors: 'Dismiss behavior',
      start_time: 'Start',
      end_time: 'End',
      apply_to_provinces: 'Applied provinces',
      image_file_name: 'Image',
    };
    return fieldLabelMap[fieldName] || fieldName.replace(/_/g, ' ');
  };

  const formatFieldValue = (fieldName: string, value: any): RenderValue => {
    if (fieldName === 'type') {
      const option = ANNOUNCEMENT_TYPE_OPTIONS.find(
        (opt) => opt.value === value,
      );
      return option?.label || value;
    }
    if (fieldName === 'apply_to_pos') {
      const option = POS_TYPE_OPTIONS.find((opt) => opt.value === value);
      return option?.label || value;
    }
    if (fieldName === 'apply_to_provinces_type') {
      const option = PROVINCE_TYPE_OPTIONS.find((opt) => opt.value === value);
      return option?.label || value;
    }
    if (fieldName === 'dismiss_behavior' || fieldName === 'dismiss_behaviors') {
      // Try to find in both popup and banner options
      let option = DISMISS_BEHAVIOR_POPUP_OPTIONS.find(
        (opt) => opt.value === value,
      );
      if (!option) {
        option = DISMISS_BEHAVIOR_BANNER_OPTIONS.find(
          (opt) => opt.value === value,
        );
      }
      return option?.label || value;
    }
    if (fieldName === 'image' && value) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <CfIcon name='image-add' size={16} color='rgba(51, 54, 61, 0.40)' />
          <Typography
            sx={{
              fontSize: 14,
              color: 'rgba(51, 54, 61, 0.40)',
            }}
          >
            {typeof value === 'string' ? value : 'Image file'}
          </Typography>
        </Box>
      );
    }
    if (fieldName === 'icon' && value) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Icon size={16} name={value} unset />
        </Box>
      );
    }
    if (fieldName === 'banner_color' && value) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Box
            sx={{
              width: 16,
              height: 16,
              backgroundColor: value,
              borderRadius: '50%',
              display: 'inline-block',
            }}
          />
        </Box>
      );
    }
    return value;
  };

  if (groupedTimeline.length === 0) {
    return null;
  }

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        color: theme.foreground.black,
      }}
    >
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: 18,
        }}
      >
        Timeline
      </Typography>

      <Stack sx={{ gap: 3 }}>
        {groupedTimeline.map((group: GroupedTimeline, groupIndex: number) => (
          // eslint-disable-next-line react/no-array-index-key
          <Box key={groupIndex}>
            {/* Date header */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr 1fr',
                gap: 2,
                mb: 1.5,
              }}
            >
              {/* Dot placeholder for alignment */}
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  flexShrink: 0,
                  visibility: 'hidden',
                }}
              />
              {/* Date */}
              <Typography
                sx={{
                  fontSize: 14,
                  color: 'rgba(51, 54, 61, 0.40)',
                }}
              >
                {formatDate(group.items[0].created_at)}
              </Typography>
            </Box>

            {/* Items in this date */}
            <Stack sx={{ gap: 1.5 }}>
              {group.items.map((item: LogItem) => {
                // eslint-disable-next-line no-underscore-dangle
                const isExpanded = expandedItems.has(item._id);
                const changes = getChangedFields(item.old_data, item.new_data);
                const hasChanges = Object.keys(changes).length > 0;

                return (
                  <Box
                    // eslint-disable-next-line no-underscore-dangle
                    key={item._id}
                  >
                    {/* Main row - clickable if has changes */}
                    <Box
                      onClick={() => {
                        // eslint-disable-next-line no-underscore-dangle
                        if (hasChanges) toggleExpand(item._id);
                      }}
                      sx={{
                        'display': 'grid',
                        'gridTemplateColumns': 'auto 1fr 1fr',
                        'gap': 2,
                        'alignItems': 'flex-start',
                        'pb': 0.5,
                        'cursor': hasChanges ? 'pointer' : 'default',
                      }}
                    >
                      {/* Dot */}
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 'rgba(51, 54, 61, 0.40)',
                          flexShrink: 0,
                          mt: '7px',
                        }}
                      />

                      {/* Left: Title with expand icon */}
                      <Stack sx={{ gap: 0.5 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 14,
                              color: '#33363D',
                            }}
                          >
                            {item.title}
                          </Typography>
                          {hasChanges && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                color: '#2D91FC',
                              }}
                            >
                              <Icon
                                name={
                                  isExpanded ? 'arrow-up-s' : 'arrow-down-s'
                                }
                                size={16}
                                color='#ADAFB1'
                              />
                            </Box>
                          )}
                        </Box>
                        {item.created_by_name && (
                          <Typography
                            sx={{
                              fontSize: 14,
                              color: 'rgba(51, 54, 61, 0.40)',
                            }}
                          >
                            By {item.created_by_name}
                          </Typography>
                        )}
                      </Stack>

                      {/* Right: Time */}
                      <Box
                        sx={{
                          textAlign: 'right',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 14,
                            color: 'rgba(51, 54, 61, 0.40)',
                          }}
                        >
                          {formatTime(item.created_at)}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Expanded content - show changes */}
                    {isExpanded && hasChanges && (
                      <Box
                        sx={{
                          ml: 3,
                          mt: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1.5,
                          width: '100%',
                        }}
                      >
                        {/* Data rows */}
                        {Object.entries(changes).map(
                          ([key, value]: [string, any]) => {
                            const oldVal = value.old;
                            const newVal = value.new;
                            const isHtmlField = key === 'message_description';

                            let displayOld: any = oldVal;
                            let displayNew: any = newVal;

                            // Format specific fields
                            if (key === 'start_time' || key === 'end_time') {
                              // start_time and end_time are already stored in timezone format
                              // Format them without timezone conversion but with proper date/time format
                              displayOld = oldVal
                                ? formatStartEndTime(oldVal)
                                : '';
                              displayNew = newVal
                                ? formatStartEndTime(newVal)
                                : '';
                            } else if (
                              Array.isArray(oldVal) ||
                              Array.isArray(newVal)
                            ) {
                              if (Array.isArray(oldVal)) {
                                displayOld =
                                  oldVal.length > 0 ? oldVal.join(', ') : '';
                              }
                              if (Array.isArray(newVal)) {
                                displayNew =
                                  newVal.length > 0 ? newVal.join(', ') : '';
                              }
                            } else {
                              // Format non-time and non-array fields
                              displayOld =
                                key === 'image'
                                  ? formatFieldValue(key, oldVal)
                                  : formatFieldValue(key, oldVal);
                              displayNew =
                                key === 'image'
                                  ? formatFieldValue(key, newVal)
                                  : formatFieldValue(key, newVal);
                            }

                            return (
                              <Box
                                key={key}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1.5,
                                  width: '100%',
                                }}
                              >
                                {/* Field name with colon */}
                                <Typography
                                  sx={{
                                    fontSize: 14,
                                    textTransform: 'capitalize',
                                    wordBreak: 'break-word',
                                    color: 'rgba(51, 54, 61, 0.40)',
                                    flexShrink: 0,
                                  }}
                                >
                                  {getFieldLabel(key)}:
                                </Typography>

                                {/* Before value */}
                                {isHtmlField && (
                                  <Box
                                    sx={{
                                      'fontSize': 14,
                                      'color': 'rgba(51, 54, 61, 0.40)',
                                      '& p': {
                                        margin: 0,
                                        fontSize: 14,
                                      },
                                      '& li': {
                                        fontSize: 14,
                                      },
                                      '& ul, & ol': {
                                        margin: 0,
                                        paddingLeft: 1.5,
                                      },
                                      'maxHeight': 150,
                                      'overflow': 'auto',
                                    }}
                                    className='ql-editor customized-scrollbar'
                                    dangerouslySetInnerHTML={{
                                      __html: displayOld || '',
                                    }}
                                  />
                                )}
                                {!isHtmlField &&
                                  typeof displayOld === 'string' && (
                                    <Typography
                                      sx={{
                                        fontSize: 14,
                                        color: 'rgba(51, 54, 61, 0.40)',
                                        wordBreak: 'break-word',
                                      }}
                                    >
                                      {displayOld || ''}
                                    </Typography>
                                  )}
                                {!isHtmlField &&
                                  typeof displayOld !== 'string' &&
                                  displayOld}

                                {/* Arrow icon */}
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#2D91FC',
                                    minHeight: 'auto',
                                    flexShrink: 0,
                                  }}
                                >
                                  <CfIcon
                                    name='arrow-right-line'
                                    size={16}
                                    color='rgba(51, 54, 61, 0.40)'
                                  />
                                </Box>

                                {/* After value */}
                                {isHtmlField && (
                                  <Box
                                    sx={{
                                      'fontSize': 14,
                                      'color': 'rgba(51, 54, 61, 0.40)',
                                      '& p': {
                                        margin: 0,
                                        fontSize: 14,
                                      },
                                      '& li': {
                                        fontSize: 14,
                                      },
                                      '& ul, & ol': {
                                        margin: 0,
                                        paddingLeft: 1.5,
                                      },
                                      'maxHeight': 150,
                                      'overflow': 'auto',
                                    }}
                                    className='ql-editor customized-scrollbar'
                                    dangerouslySetInnerHTML={{
                                      __html: displayNew || '',
                                    }}
                                  />
                                )}
                                {!isHtmlField &&
                                  typeof displayNew === 'string' && (
                                    <Typography
                                      sx={{
                                        fontSize: 14,
                                        color: 'rgba(51, 54, 61, 0.40)',
                                        wordBreak: 'break-word',
                                      }}
                                    >
                                      {displayNew || ''}
                                    </Typography>
                                  )}
                                {!isHtmlField &&
                                  typeof displayNew !== 'string' &&
                                  displayNew}
                              </Box>
                            );
                          },
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Card>
  );
};

export default TimelineSection;
